import React, { useState } from "react";
import { Card, Group, Tabs, Text, Title, Space } from "@mantine/core";
import { IconReportSearch } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import {
  useParams,
  useSearchParams,
  Route,
  Routes,
  Link,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import { EffortLocationJoinList } from "@components/Effort";
import { KeywordManagement } from "@components/Keyword";
import { ContestItem, ContestList } from "@components/Contest";
import { QrCodeGenerator, Subnav } from "@components/shared";
import {
  RideChallengeManagement,
  RideChallengeVerificationCodes,
} from "@components/RideChallenge";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import formatDisplayDate from "@util/formatDisplayDate";

const SectionTitle = ({ children }) => (
  <Title order={4} mb="sm">
    {children}
  </Title>
);

export default function EffortReadOnly({ effortInfo }) {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(false);

  const managerInfo = useSelector((state) => state.manager);

  const isRideChallenge = effortInfo.variety === 6;
  const isVehicleDataEffort = effortInfo.variety === 7;
  const hasWizardSettings = JSON.stringify(effortInfo.wizard_settings) !== "{}";
  const showQr =
    !hasWizardSettings ||
    (effortInfo.wizard_settings &&
      effortInfo.wizard_settings.microsite === true &&
      !isVehicleDataEffort);
  const showKeyword =
    !hasWizardSettings ||
    (effortInfo.wizard_settings &&
      effortInfo.wizard_settings.show_keywords === true);

  const formattedStartDate = formatDisplayDate({
    date: `${effortInfo.start_date}T${effortInfo.start_time}`,
    timezone: effortInfo.timezone,
  });
  const formattedEndDate = formatDisplayDate({
    date: `${effortInfo.end_date}T${effortInfo.end_time}`,
    timezone: effortInfo.timezone,
  });

  const ContestAddOnly = () => (
    <ContestList
      isManager
      readOnly
      refresh={refresh}
      showVarietyFilters={effortInfo.variety !== 5}
      requestInfo={{
        url: `/retrieve-contests/`,
        data: {
          campaign_effort_id: effortInfo.id,
        },
      }}
      displayFormatter={(contest) => (
        <ContestItem key={contest.id} contestInfo={contest} readOnly />
      )}
    />
  );

  return (
    <div>
      <Subnav
        buttonColor={entityColor.effort}
        links={[
          { text: t("common:detail"), to: `/efforts/${effortInfo.id}` },
          {
            text: t("mixer:contest_plural"),
            to: `/efforts/${effortInfo.id}/contests`,
            visible: isVehicleDataEffort ? false : true,
          },
          {
            text: t("mixer:keyword_plural"),
            to: `/efforts/${effortInfo.id}/keywords`,
            visible: showKeyword ? true : false,
          },
          {
            text: t("common:management"),
            to: `/efforts/${effortInfo.id}/ride-challenge`,
          },
          {
            text: t("mixer:verification_code_plural"),
            to: `/efforts/${effortInfo.id}/verification`,
            visible: isRideChallenge || isVehicleDataEffort ? true : false,
          },
        ].filter((f) => f.visible !== false)}
      />
      <Routes>
        <Route
          exact
          path="/"
          element={
            <>
              <Card>
                {formattedStartDate && formattedEndDate && (
                  <React.Fragment>
                    {/* <SectionTitle>Dates</SectionTitle> */}
                    <Group spacing="xs">
                      {entityIcon.calendar(24)}
                      <Text>
                        {formattedStartDate} - {formattedEndDate}
                      </Text>
                    </Group>
                  </React.Fragment>
                )}
                {showQr && (
                  <React.Fragment>
                    <Space mb="xl" />
                    <SectionTitle>QR Code</SectionTitle>
                    <QrCodeGenerator
                      text={`${import.meta.env.VITE_DOMAIN}/entry/${
                        effortInfo.microsite_url
                      }`}
                    />
                  </React.Fragment>
                )}
              </Card>
              {(isRideChallenge || isVehicleDataEffort) && (
                <Card mt="lg">
                  <EffortLocationJoinList
                    effortId={effortInfo.id}
                    locationId={managerInfo.location_id}
                    isRideChallenge
                  />
                </Card>
              )}
            </>
          }
        />
        {!isVehicleDataEffort && (
          <Route
            path="/contests"
            element={
              <>
                <ContestAddOnly />
              </>
            }
          />
        )}
        {showKeyword && (
          <Route
            path="/keywords"
            element={
              <>
                <KeywordManagement
                  minStartDate={effortInfo.start_date}
                  maxEndDate={effortInfo.end_date}
                  effortId={effortInfo.id}
                  showCreate={false}
                />
              </>
            }
          />
        )}
        {(isRideChallenge || isVehicleDataEffort) && [
          <Route
            path="/ride-challenge"
            key="ride-challenge"
            element={
              <>
                <RideChallengeManagement
                  effortId={effortInfo.id}
                  locationId={managerInfo.location_id}
                />
              </>
            }
          />,
          <Route
            path="/verification"
            key="verification"
            element={
              <>
                <RideChallengeVerificationCodes
                  effortId={effortInfo.id}
                  effortName={effortInfo.title}
                  fetchAlerts={() => true}
                  locationId={managerInfo.location_id}
                />
              </>
            }
          />,
        ]}
      </Routes>
    </div>
  );
}
